import React, { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, X } from "lucide-react";
import { COLORS } from "@/components/zone/constants";

const fontSerif = "Georgia, 'Times New Roman', serif";

/**
 * UnderConstruction — branded modal overlay shown for locked TAB areas
 * (Studio / Lounge / Boardroom / Merch). Layers above the Campus Quad
 * WebGL canvas without dismounting it; closes back to "/" so the
 * scene stays interactive once dismissed.
 */
export default function UnderConstruction({ area = "This area" }) {
  const navigate = useNavigate();

  const handleClose = useCallback(() => {
    navigate("/");
  }, [navigate]);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleClose]);

  // Disable body scroll while open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Stop click events from reaching the WebGL canvas while modal is open.
  const stop = (e) => e.stopPropagation();

  return (
    <div
      data-testid="under-construction-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="uc-title"
      onClick={handleClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9000,
        background: "rgba(5,10,28,0.72)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        display: "grid",
        placeItems: "center",
        animation: "tab-uc-fade-in .28s ease-out",
        fontFamily: fontSerif,
        color: COLORS.ivory,
      }}
    >
      <style>
        {`
          @keyframes tab-uc-fade-in {
            from { opacity: 0; }
            to   { opacity: 1; }
          }
          @keyframes tab-uc-pop-in {
            from { opacity: 0; transform: translateY(12px) scale(.96); }
            to   { opacity: 1; transform: translateY(0)    scale(1); }
          }
        `}
      </style>

      <div
        data-testid="under-construction-card"
        onClick={stop}
        style={{
          position: "relative",
          width: "min(92vw, 480px)",
          background:
            "linear-gradient(180deg, rgba(11,21,48,0.94) 0%, rgba(15,31,74,0.94) 100%)",
          border: `1px solid ${COLORS.gold}`,
          borderRadius: 18,
          padding: "44px 40px 36px",
          boxShadow:
            "0 24px 80px rgba(0,0,0,0.65), 0 0 70px rgba(230,180,80,0.18)",
          textAlign: "center",
          animation: "tab-uc-pop-in .35s cubic-bezier(.2,.7,.3,1)",
        }}
      >
        {/* Close X */}
        <button
          data-testid="under-construction-close"
          onClick={handleClose}
          aria-label="Close"
          style={{
            position: "absolute",
            top: 14,
            right: 14,
            background: "transparent",
            border: "none",
            color: COLORS.ivory,
            opacity: 0.7,
            cursor: "pointer",
            padding: 6,
            borderRadius: 999,
            display: "grid",
            placeItems: "center",
          }}
        >
          <X size={16} />
        </button>

        {/* Lock crest */}
        <div
          aria-hidden="true"
          style={{
            width: 68,
            height: 68,
            margin: "0 auto 20px",
            borderRadius: "50%",
            border: `2px solid ${COLORS.gold}`,
            display: "grid",
            placeItems: "center",
            color: COLORS.gold,
            background: "rgba(11,21,48,0.7)",
            boxShadow: `inset 0 0 20px rgba(230,180,80,0.18)`,
          }}
        >
          <Lock size={28} />
        </div>

        <div
          data-testid="under-construction-eyebrow"
          style={{
            fontSize: 10,
            letterSpacing: 5,
            color: COLORS.gold,
            textTransform: "uppercase",
            marginBottom: 12,
          }}
        >
          {area} · Coming Soon
        </div>

        <h2
          id="uc-title"
          data-testid="under-construction-title"
          style={{
            margin: 0,
            fontSize: 30,
            color: COLORS.goldBright,
            letterSpacing: 0.5,
            lineHeight: 1.15,
          }}
        >
          Under Construction
        </h2>

        <div
          style={{
            width: 64,
            height: 2,
            background: COLORS.gold,
            margin: "22px auto",
            opacity: 0.85,
          }}
        />

        <p
          data-testid="under-construction-body"
          style={{
            fontSize: 16,
            lineHeight: 1.65,
            opacity: 0.92,
            fontStyle: "italic",
            margin: "0 0 26px",
          }}
        >
          This area is under construction. Please check back later!
        </p>

        <button
          data-testid="under-construction-return"
          onClick={handleClose}
          style={{
            padding: "12px 26px",
            borderRadius: 999,
            border: "none",
            cursor: "pointer",
            backgroundColor: COLORS.gold,
            color: COLORS.navyDeep,
            fontFamily: fontSerif,
            fontSize: 12,
            letterSpacing: 2.5,
            textTransform: "uppercase",
            fontWeight: 700,
            boxShadow: "0 8px 24px rgba(230,180,80,0.25)",
          }}
        >
          Return to the Quad
        </button>

        <div
          style={{
            marginTop: 22,
            fontSize: 10,
            letterSpacing: 3,
            color: "rgba(243,234,212,0.55)",
            textTransform: "uppercase",
          }}
        >
          Press Esc to dismiss
        </div>
      </div>
    </div>
  );
}
